import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Box, Flex } from "./Basics";
import LittleButton from "./LittleButton";
import useWindowWidth from "../hooks/useWindowWidth";
import Link from "next/link";
import SVGIcons from './SVGIcons';
import Logo from "./Logo";

const FooterContainer = styled(Box)`
  width: 100%;
  height: 251px;
  box-sizing: border-box;
  color: #222222;
  background: #e8e8e8;
  position: relative;
  align-items: flex-start;
  justify-content: center;
  font-size: 1.6rem;

  @media screen and (max-width: 1400px) {
    padding: 0 30px;
  }

  @media only screen and (max-width: 930px) {
    height: 650px;
  }

  @media only screen and (max-width: 600px) {
    height: 519px;
  }
`;

const NavLinkFooter = styled(Flex)`
  flex: 1;
  position: absolute;
  display: flex;
  gap: 60px;
  bottom: 40px;
  right: 200px;

  a {
    text-decoration: none;
    color: #222222;
    font-size: 14px;
  }

  @media screen and (max-width: 1400px) {
    right: 30px;
  }

  @media screen and (max-width: 1040px) {
    gap: 10px;
  }

  @media screen and (max-width: 930px) {
    flex-direction: column;
    gap: 20px;
    left: -20px;
    bottom: 20px;
  }

  @media only screen and (max-width: 600px) {
    left: -10px;
  }
`;

const LineDivided = styled.div`
    display: none;
    
    @media only screen and (max-width: 930px) {
        display: block;
        width: 75%;
        height: 1px;
        background: #156498;
    }
`;

const LinkList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 15px;
  list-style: none;
  padding: 0;
  margin-left: 50px;

  a:hover {
    color: #3ea78d;
    font-weight: 900;
    transition: 0.3s ease-in-out;
  }

  @media only screen and (max-width: 930px) {
    margin-left: 30px;
  }

  @media only screen and (max-width: 600px) {
    margin-left: 20px;
    font-size: 14px;
    gap: 10px;
  }
`;

const NavSocialMedias = styled(Flex)`
  margin-top: 20px;
  gap: 20px;

  @media only screen and (max-width: 600px) {
    gap: 15px;
    margin-top: 10px;
  }
`;

const FooterContacts = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 220px;
  margin-left: 180px;
  @media only screen and (max-width: 1400px) {
    margin-left: -20px;
  }
  @media only screen and (max-width: 600px) {
    height: 180px;
  }
`;

const IconButton = styled.div`
  height: 31px;
  width: 31px;

  @media only screen and (max-width: 600px) {
    position: absolute;
    top: 0;
    height: 15px;
    width: 15px;
  }
`;

const NavLinkDivided = styled.div`
  display: none;

  @media only screen and (max-width: 930px) {
    display: block;  
    height: 1px;  
    width: 75%;
    background: #156498;
  }
`;

const DivPhone = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
  font-size: 18px;

  span:hover {
    color: #3ea78d;
  }

  @media only screen and (max-width: 600px) {
    font-size: 14px;
  }
`;

const Footer: React.FC = () => {
  const [isClient, setIsClient] = useState(false);
  const windowWidth = useWindowWidth();
  const [dimensions, setDimensions] = useState({ width: 25 });
  const [hoveredIcon, setHoveredIcon] = useState<string | null>(null);

  useEffect(() => {
    if (isClient) {
      const width = windowWidth <= 600 ? 18 : 25;
      setDimensions({ width });
    }
  }, [isClient, windowWidth]);

  const handleMouseEnter = (icon: string) => setHoveredIcon(icon);
  const handleMouseLeave = () => setHoveredIcon(null);

  // funcao que troca as cores dos icons
  const getIconColor = (icon: string) =>
    hoveredIcon === icon ? "#3ea78d" : "#156498";

  const WhatsAppUrl = "https://api.whatsapp.com/send?text=Ol%C3%A1%20gostaria%20de%20receber%20mais%20informa%C3%A7%C3%B5es%20sobre%20a%20plataforma.&phone=5511973459775";
  const InstaUrl = "https://www.instagram.com/webproject.br/";
  const LinkedInUrl = "https://www.linkedin.com/company/webproject";
  const FacebookUrl = "https://www.facebook.com/webproject.br";
  const TwitterUrl = "https://twitter.com/webproject_br";

  const handleScrollUp = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  const footerLinks = [
    { title: "ÍNICIO", href: "https://webproject.com.br", external: true },
    { title: "CASOS DE SUCESSO", href: `/`, external: false },
    {
      title: "EQUIPE",
      href: "https://webproject.com.br/equipe/",
      external: true,
    },
    {
      title: "VAGAS",
      href: "https://webproject.com.br/vagas/",
      external: true,
    },
    {
      title: "ENTRAR",
      href: "https://webproject.com.br/login/",
      external: true,
    },
    {
      title: "CADASTRE-SE",
      href: "https://webproject.com.br/usuarios/registro/",
      external: true,
    },
    {
      title: "CONTATE-NOS",
      href: "https://webproject.com.br/contato/",
      external: true,
    },
    {
      title: "CENTRAL DE AJUDA",
      href: "https://webproject.com.br/support/",
      external: true,
    },
    {
      title: "COORDENANDO PROJETOS",
      href: "https://webproject.com.br/blog/",
      external: true,
    },
  ];

  return (
    <FooterContainer>
      {isClient && (
        <LittleButton
          aria-label={"ir para o topo"}
          $positionTop="0"
          $positionLeft="50%"
          $bgColor="#1c5386"
          $hoverColor="linear-gradient(142.17deg, #1C5386 7.89%, #3EA78D 148.64%)"
          $size={windowWidth <= 600 ? "28px" : "57px"}
          onClick={handleScrollUp}
        >
          <IconButton>
            <SVGIcons type='ARROW' height='100%' width='100%' />
          </IconButton>
        </LittleButton>
      )}
      <Logo />
      <FooterContacts>
        <a
          href="tel: +55 11 3230-9775"
          onMouseEnter={() => handleMouseEnter("phone")}
          onMouseLeave={handleMouseLeave}
        >
          <DivPhone>
            <SVGIcons
              type='PHONE_ICON'
              color={getIconColor("phone")}
              width={dimensions.width}
              height={25}
            />
            <span>+55 (11) 3230-9775</span>
          </DivPhone>
        </a>

        <NavSocialMedias>
          <a
            aria-label="Nos chame no nosso Whatsapp"
            href={WhatsAppUrl}
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => handleMouseEnter("whatsapp")}
            onMouseLeave={handleMouseLeave}
          >
            <SVGIcons
              type='WHATSAPP_ICON'
              color={getIconColor("whatsapp")}
              width={dimensions.width}
              height={25}
            />
          </a>
          <a
            aria-label="Nos siga no Instagram"
            href={InstaUrl}
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => handleMouseEnter("instagram")}
            onMouseLeave={handleMouseLeave}
          >
            <SVGIcons
              type='INSTAGRAM_ICON'
              color={getIconColor("instagram")}
              width={dimensions.width}
              height={25}
            />
          </a>
          <a
            aria-label="Nos siga no Facebook"
            href={FacebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => handleMouseEnter("facebook")}
            onMouseLeave={handleMouseLeave}
          >
            <SVGIcons
              type='FACEBOOK_ICON'
              color={getIconColor("facebook")}
              width={dimensions.width}
              height={25}
            />
          </a>
          <a
            aria-label="Nos siga no LinkedIn"
            href={LinkedInUrl}
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => handleMouseEnter("linkedin")}
            onMouseLeave={handleMouseLeave}
          >
            <SVGIcons
              type='LINKEDIN_ICON'
              color={getIconColor("linkedin")}
              width={dimensions.width}
              height={25}
            />
          </a>
          <a
            aria-label="Nos siga no X/Twitter"
            href={TwitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => handleMouseEnter("twitter")}
            onMouseLeave={handleMouseLeave}
          >
            <SVGIcons
              type='TWITTER_ICON'
              color={getIconColor("twitter")}
              width={dimensions.width}
              height={30}
            />
          </a>
        </NavSocialMedias>
      </FooterContacts>
      <NavLinkFooter>
        <LineDivided/>
        <LinkList>
          {footerLinks.slice(0, 3).map((link, linkIndex) => (
            <li key={linkIndex}>
              {link.external ? (
                <a href={link.href} target="_blank" rel="noopener noreferrer">
                  {link.title}
                </a>
              ) : (
                <Link href={link.href} passHref>
                  {link.title}
                </Link>
              )}
            </li>
          ))}
        </LinkList>
        <NavLinkDivided />
        <LinkList>
          {footerLinks.slice(3, 6).map((link, linkIndex) => (
            <li key={linkIndex}>
              {link.external ? (
                <a href={link.href} target="_blank" rel="noopener noreferrer">
                  {link.title}
                </a>
              ) : (
                <Link href={link.href} passHref>
                  {link.title}
                </Link>
              )}
            </li>
          ))}
        </LinkList>
        <NavLinkDivided />
        <LinkList>
          {footerLinks.slice(6, 9).map((link, linkIndex) => (
            <li key={linkIndex}>
              {link.external ? (
                <a href={link.href} target="_blank" rel="noopener noreferrer">
                  {link.title}
                </a>
              ) : (
                <Link href={link.href} passHref>
                  {link.title}
                </Link>
              )}
            </li>
          ))}
        </LinkList>
        <LineDivided/>
      </NavLinkFooter>
    </FooterContainer>
  );
};

export default Footer;